import { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAQSection = () => {
  const [faqs, setFaqs] = useState([]);
  const [openIndex, setOpenIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const response = await axios.get(`${API}/faqs`);
        setFaqs(response.data);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
        // Use default FAQs on error
        setFaqs([
          {
            id: '1',
            question: 'How long does a typical basement renovation take?',
            answer: 'Most basement developments take between 6-10 weeks, depending on the scope of work. We provide a detailed timeline during our initial consultation so you know exactly what to expect.',
          },
          {
            id: '2',
            question: 'Do you offer free estimates?',
            answer: 'Yes! We offer free, no-obligation estimates for all projects. We\'ll visit your home, discuss your vision, and provide a detailed quote within 48 hours.',
          },
          {
            id: '3',
            question: 'Are you licensed and insured?',
            answer: 'Absolutely. We are fully licensed and insured in Alberta, and all our work meets or exceeds local building codes. We pull all necessary permits and arrange inspections.',
          },
          {
            id: '4',
            question: 'What areas do you serve?',
            answer: 'We serve Calgary and surrounding communities including Airdrie, Cochrane, Chestermere, and Okotoks.',
          },
          {
            id: '5',
            question: 'Can you help with legal basement suite permits?',
            answer: 'Yes, we have extensive experience with legal basement suite developments and can guide you through the entire permit process with the City of Calgary.',
          },
          {
            id: '6',
            question: 'What is your payment structure?',
            answer: 'We typically work with a deposit to secure your project date, followed by progress payments at key milestones. We never ask for full payment upfront.',
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchFAQs();
  }, []);

  return (
    <section className="py-24 md:py-32 bg-[#f8f9fa]" data-testid="faq-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column - Header */}
          <div>
            <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
              Common Questions
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#0f172a] mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600 text-lg mb-8">
              Have questions about your renovation project? We've compiled answers to the most 
              common questions we receive from Calgary homeowners.
            </p>
            <div className="bg-white p-6 border-l-4 border-[#c5a059]">
              <p className="text-slate-600 mb-4">
                Don't see your question here? We're happy to help.
              </p>
              <a
                href="tel:403-402-6596"
                className="text-[#0f172a] font-semibold hover:text-[#c5a059] transition-colors"
                data-testid="faq-phone-link"
              >
                Call us at 403-402-6596
              </a>
            </div>
          </div>

          {/* Right Column - FAQ Accordion */}
          <div className="space-y-4">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <div key={i} className="bg-white p-6 animate-pulse">
                  <div className="h-6 bg-slate-200 rounded w-3/4" />
                </div>
              ))
            ) : (
              faqs.map((faq, index) => (
                <div
                  key={faq.id}
                  className="bg-white border border-slate-100 overflow-hidden"
                  data-testid={`faq-item-${faq.id}`}
                >
                  <button
                    className="w-full px-6 py-5 flex items-center justify-between text-left"
                    onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                    aria-expanded={openIndex === index}
                    data-testid={`faq-toggle-${faq.id}`}
                  >
                    <span className="font-semibold text-[#0f172a] pr-4">
                      {faq.question}
                    </span>
                    <ChevronDown
                      size={20}
                      className={`text-[#c5a059] flex-shrink-0 transition-transform duration-300 ${
                        openIndex === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openIndex === index ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <div className="px-6 pb-6 text-slate-600">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
